import MandatoryIcon from 'components/icons/MandatoryIcon';
import i18next from 'i18next';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ActionState,
  ConversionFactorToMassModel,
  EPDDeveloperModel,
  EPDModel,
  EPDVerifierModel,
  FieldState,
  ReferenceFlowOldWizardModel,
  VerificationType,
  VersionStateType,
} from 'services/EpdClient';
import { EPDBasedOnUnitEnum, EPDProductType } from 'types/types';
import * as Yup from 'yup';

import { WizardStepValidationSchema } from './types';

export function Validation(props: { validation: WizardStepValidationSchema; field: string | string[]; epd?: EPDModel }) {
  const { t } = useTranslation();
  const iconId = useId();

  if (props.epd == null) {
    return null;
  }

  const fields = Array.isArray(props.field) ? props.field : props.field.split('|').map((f) => f.trim());
  for (const singleField of fields) {
    try {
      props.validation.validateSyncAt(singleField, props.epd);
    } catch (error: any) {
      const fieldState = (props.epd as unknown as Record<string, unknown>)[`${singleField}State`] as FieldState | undefined;
      const severity = fieldState?.errorSeverity;
      const messageExists = i18next.exists(error.message);

      const message = messageExists
        ? t(error.message)
        : // : !error.message.includes('required field') && error.message !== 'Required' // rem due to testing and awaiting decision
          // ? error.message
          undefined;

      // only error sign, awaiting severity display decision and design
      // if (severity === 'warning') {
      //   return (
      //     <>
      //       <WarningTriangleIcon id={iconId} />
      //       {message && (
      //         <Tooltip targetId={iconId} position="right" offset={4}>
      //           {message}
      //         </Tooltip>
      //       )}
      //     </>
      //   );
      // }

      return <MandatoryIcon message={message} />;
    }
  }

  return null;
}

export const isNullOrEmptyHtml = (value: any) => {
  if (value === null || value === undefined) {
    return true;
  }

  const valueWithoutPTag = value.replace(/(<([^>]+)>)/gi, '');

  const stringWithSpaces = valueWithoutPTag.replace(/&nbsp;/g, ' ');

  return stringWithSpaces.trim().length === 0;
};

// Validation Helpers
export const validatedString = (prop: keyof EPDModel | string) => ({
  [prop]: Yup.string()
    .nullable()
    .when([`${prop}State`], (state: FieldState, schema: Yup.StringSchema) => {
      let result = state.mandatory
        ? schema.test('not-empty-or-empty-html-tags', 'Required', (value) => {
            if (!value || value.trim() === '' || value.trim() === '<p></p>' || isNullOrEmptyHtml(value)) {
              return false;
            }
            return true;
          })
        : schema.nullable();

      if (state?.errorMessage && state.errorMessage.trim() !== '' && !isNullOrEmptyHtml(state.errorMessage)) {
        result = result.test('backend-validation-error', state.errorMessage, () => false);
      }

      return result;
    }),
});

export const mustBeEmptyErrorString = (prop: keyof EPDModel | string) => ({
  [prop]: Yup.string().when([`${prop}State`], (state: FieldState, schema: Yup.StringSchema) =>
    schema
      .test('must-be-empty-error-message', state?.errorMessage || 'Must be empty', () => {
        return (
          state?.errorMessage == null ||
          state.errorMessage.trim() === '' ||
          state.errorMessage.trim() === '<p></p>' ||
          isNullOrEmptyHtml(state.errorMessage)
        );
      })
      .nullable()
  ),
});

export const validatedDate = (prop: keyof EPDModel) => ({
  [prop]: Yup.date()
    .transform((value, input) => {
      if ((input ?? '') === '') return null;
      if (isNaN(value)) return null;
      if (value.toISOString().slice(0, 10) === '1900-12-31') return null;
      return new Date(value);
    })
    .when([`${prop}State`], (state: FieldState, schema: Yup.DateSchema) =>
      state.mandatory ? schema.required() : schema.nullable()
    ),
});

export const validatedArray = (prop: keyof EPDModel) => ({
  [prop]: Yup.array().when([`${prop}State`], (state: FieldState, schema: Yup.ArraySchema<any>) =>
    state.mandatory ? schema.min(1) : schema.nullable()
  ),
});

export const validatedEnum = (prop: keyof EPDModel, enumValues: any) => ({
  [prop]: Yup.mixed().when([`${prop}State`], (state: FieldState, schema: Yup.MixedSchema) =>
    state.mandatory ? schema.oneOf(Object.values(enumValues)) : schema.nullable()
  ),
});

export const validatedDocuments = (prop: keyof EPDModel) => ({
  [prop]: Yup.array()
    .when([`${prop}State`], (state: FieldState, schema: Yup.ArraySchema<any>) =>
      state.mandatory ? schema.min(1) : schema.nullable()
    )
    .test('contains-epd-document', 'At least one document must be the type of "EPD document"', (documents) => {
      if (!documents || !documents?.length) return true;
      return documents.some((doc: any) => doc.name.includes('EPD document'));
    }),
});

export const validatedDataSourcesDictionary = (prop: keyof EPDModel) => ({
  [prop]: Yup.array().when([`${prop}State`], (state: FieldState, schema: Yup.ArraySchema<any>) =>
    schema
      .of(
        Yup.object().shape({
          dataSource: Yup.object()
            .shape({
              id: Yup.string().required('DataSource id is required'),
              name: Yup.string().required('DataSource name is required'),
              isDisabled: Yup.boolean(),
            })
            .test(
              'dataSource-not-disabled',
              'Selected data source is disabled',
              (value) => value !== undefined && value !== null && value.isDisabled === false
            ),
          dataSourceVersion: Yup.object()
            .shape({
              id: Yup.string().required('DataSourceVersion id is required'),
              name: Yup.string().required('DataSourceVersion name is required'),
              isDisabled: Yup.boolean(),
            })
            .test(
              'dataSourceVersion-not-disabled',
              'Selected data source version is disabled',
              (value) => value !== undefined && value !== null && value.isDisabled === false
            ),
        })
      )
      .when([], {
        is: () => state.mandatory === true,
        then: schema.min(1, 'At least one data source is required'),
        otherwise: schema.nullable(),
      })
  ),
});

export const validatedSubObject = (prop: keyof EPDModel) => ({
  [prop]: Yup.object().when([`${prop}State`], (state: FieldState, schema: Yup.ObjectSchema) =>
    state.mandatory ? schema.typeError('Required').shape({ id: Yup.string().required() }) : schema.nullable()
  ),
});

const transformInvalidNumberToNull = (value: number | null, originalValue: unknown) => {
  if (originalValue === '' || originalValue == null) {
    return null;
  }

  return Number.isNaN(value) ? null : value;
};

export const validatedNumber = (prop: keyof EPDModel) => ({
  [prop]: Yup.number()
    .transform(transformInvalidNumberToNull)
    .nullable()
    .when([`${prop}State`], (state: FieldState, schema: Yup.NumberSchema) =>
      state.mandatory ? schema.required() : schema.nullable()
    ),
});

const validatedEPDDeveloper = (prop: keyof EPDModel) => ({
  [prop]: Yup.array()
    .when([`${prop}State`], (state: FieldState, schema: Yup.ArraySchema<any>) =>
      state.mandatory ? schema.required() : schema.nullable()
    )
    .test({
      name: 'not-same-verifier-company',
      message: 'epdWizard.wizardStep1.properties.epdDeveloper.messages.sameCompany',
      test: function (value: any) {
        const epdVerifier = this.parent.epdVerifier as EPDVerifierModel;

        return (
          !value ||
          !epdVerifier ||
          value.length === 0 ||
          !value.find(
            (developer: EPDDeveloperModel) =>
              developer.companyId === epdVerifier.companyId &&
              developer.contactUserId !== epdVerifier.contactUserId &&
              !epdVerifier.isExternalMembership
          )
        );
      },
    })
    .test({
      name: 'not-same-verifier-user',
      message: 'epdWizard.wizardStep1.properties.epdDeveloper.messages.sameUser',
      test: function (value: any) {
        const epdVerifier = this.parent.epdVerifier as EPDVerifierModel;

        return (
          !value ||
          !epdVerifier ||
          value.length === 0 ||
          !value.find((developer: EPDDeveloperModel) => developer.contactUserId === epdVerifier.contactUserId)
        );
      },
    }),
});

const validatedEPDVerifier = (
  prop: keyof EPDModel,
  verificationType?: VerificationType,
  verifiers?: EPDVerifierModel[],
  isLoadingVerifiers?: boolean
) => ({
  [prop]: Yup.object()
    .when([`${prop}State`], (state: FieldState, schema: Yup.ObjectSchema) =>
      state.mandatory ? schema.typeError('Required').shape({ id: Yup.string().required() }) : schema.nullable()
    )
    .test({
      name: 'not-same-developer-company',
      message: 'epdWizard.wizardStep1.properties.epdVerifier.messages.sameCompany',
      test: function (value: any) {
        const epdDevelopers = this.parent.epdDevelopers as EPDDeveloperModel[];

        return (
          !value ||
          !epdDevelopers ||
          epdDevelopers.length === 0 ||
          !epdDevelopers.find(
            (developer: EPDDeveloperModel) =>
              developer.companyId === value.companyId &&
              developer.contactUserId !== value.contactUserId &&
              !value.isExternalMembership
          )
        );
      },
    })
    .test({
      name: 'not-same-developer',
      message: 'epdWizard.wizardStep1.properties.epdVerifier.messages.sameUser',
      test: function (value: any) {
        const epdDevelopers = this.parent.epdDevelopers as EPDDeveloperModel[];

        return (
          !value ||
          !epdDevelopers ||
          epdDevelopers.length === 0 ||
          !epdDevelopers.find((developer: EPDDeveloperModel) => developer.contactUserId === value.contactUserId)
        );
      },
    })
    .test({
      name: 'excluded-from-company',
      message: 'epdWizard.wizardStep1.properties.epdVerifier.messages.notAvailable',
      test: function (value: any) {
        if (!verificationType || verificationType === VerificationType.EPDProcessCertification) {
          return true;
        }
        if (!value || !verifiers) {
          return true;
        }
        if (isLoadingVerifiers) {
          return true;
        }

        const parent = this.parent as EPDModel;
        const currentVersionState = (parent.versionState as VersionStateType) || VersionStateType.Draft;
        const statesWhereHavingMissingVerifierIsFine = [
          VersionStateType.Approved,
          VersionStateType.SentForPublication,
          VersionStateType.SentForPublicationEditorial,
          VersionStateType.Deregistered,
          VersionStateType.SentForPublicationLicenseeReview,
          VersionStateType.Published,
          VersionStateType.UnderReview,
          VersionStateType.Depublished,
          VersionStateType.DeclinedPublication,
        ];
        if (statesWhereHavingMissingVerifierIsFine.includes(currentVersionState)) {
          return true;
        }

        const isVerifierAvailable = verifiers.some((v) => v.contactUserId === value.contactUserId);
        return isVerifierAvailable;
      },
    }),
});

export const validatedValidityDate = (prop: keyof EPDModel) => ({
  [prop]: Yup.date()
    .transform((value, input) => {
      if ((input ?? '') === '') return null;
      if (isNaN(value)) return null;
      if (value.toISOString().slice(0, 10) === '1900-12-31') return null;
      return new Date(value);
    })
    .when([`${prop}State`], (state: FieldState, schema: Yup.DateSchema) =>
      state.mandatory ? schema.required() : schema.nullable()
    )
    .test({
      name: 'later-than-version-date',
      message: 'epdWizard.wizardStep4.properties.validityDate.messages.laterThanVersionDate',
      test: function (value: any) {
        const { versionDate } = this.parent;

        if (!versionDate) {
          return true;
        }

        const validityDateObj = new Date(value).setHours(0, 0, 0, 0);
        const versionDateObj = new Date(versionDate).setHours(0, 0, 0, 0);

        return !this.parent.validityDateState.mandatory || (value && validityDateObj > versionDateObj);
      },
    }),
});

export const validatedSubtype = (prop: keyof EPDModel) => ({
  [prop]: Yup.object().when([`${prop}State`], (state: FieldState, schema: Yup.ObjectSchema) => {
    return state.mandatory ? schema.typeError('Required').shape({ id: Yup.string().required() }) : schema.nullable();
  }),
});

export const validatedProductType = (prop: keyof EPDModel) => ({
  [prop]: Yup.mixed().when([`${prop}State`], (state: FieldState, schema: Yup.MixedSchema) =>
    state.mandatory && state.actionState !== ActionState.Disabled
      ? schema.oneOf(Object.values(EPDProductType))
      : schema.nullable()
  ),
});

export const validateReferenceFlow = (prop: keyof EPDModel) => {
  return {
    [prop]: Yup.object().when([`${prop}State`], (state: FieldState, schema: Yup.ObjectSchema) =>
      state.mandatory
        ? schema.typeError('Required').shape<ReferenceFlowOldWizardModel>({
            name: Yup.string().required(),
            flowPropertyId: Yup.string().required(),
            conversionFactor: Yup.number().transform(transformInvalidNumberToNull).nullable().required(),
          })
        : schema
    ),
  };
};

export const validatedConversionFactorToMass = (prop: keyof EPDModel) => {
  return {
    [prop]: Yup.object().when([`${prop}State`], (state: FieldState, schema: Yup.ObjectSchema) =>
      state.mandatory
        ? schema.typeError('Required').shape<ConversionFactorToMassModel>({
            conversionFactor: Yup.number()
              .transform(transformInvalidNumberToNull)
              .nullable()
              .required()
              .test(
                'greater-than-zero',
                'epdCompiler.step3.lcaSpecificationTab.properties.conversionFactor.validationMessageGreaterThanZero',
                (value) => {
                  if (value == null) return true;
                  return value > 0;
                }
              ),
          })
        : schema
    ),
  };
};

export const validatedBiogenicCarbonContent = (prop: keyof EPDModel) => {
  return {
    [prop]: Yup.object().shape({
      doesProductContainMoreThan5Percent: Yup.string().when(
        'doesProductContainMoreThan5PercentState',
        (state: FieldState, schema: Yup.StringSchema) => {
          return state.mandatory ? schema.required() : schema.nullable();
        }
      ),
      doesPackagingMaterialContainMoreThan5Percent: Yup.string().when(
        'doesPackagingMaterialContainMoreThan5PercentState',
        (state: FieldState, schema: Yup.StringSchema) => {
          return state.mandatory ? schema.required() : schema.nullable();
        }
      ),
      doesProductContainMoreThan5PercentState: Yup.object({
        mandatory: Yup.boolean().default(false),
      }),
      doesPackagingMaterialContainMoreThan5PercentState: Yup.object({
        mandatory: Yup.boolean().default(false),
      }),
      productItem: Yup.object()
        .shape({
          conversionFactor: Yup.number().when('conversionFactorState', (state: FieldState, schema: Yup.NumberSchema) => {
            return state.mandatory ? schema.required() : schema.nullable();
          }),
          unitKey: Yup.string().when('unitKeyState', (state: FieldState, schema: Yup.StringSchema) => {
            return state.mandatory ? schema.required() : schema.nullable();
          }),
          conversionFactorState: Yup.object({
            mandatory: Yup.boolean().default(false),
          }),
          unitKeyState: Yup.object({
            mandatory: Yup.boolean().default(false),
          }),
        })
        .nullable(),
      accompanyingPackageItem: Yup.object()
        .shape({
          conversionFactor: Yup.number().when('conversionFactorState', (state: FieldState, schema: Yup.NumberSchema) => {
            return state.mandatory ? schema.required() : schema.nullable();
          }),
          unitKey: Yup.string().when('unitKeyState', (state: FieldState, schema: Yup.StringSchema) => {
            return state.mandatory ? schema.required() : schema.nullable();
          }),
          conversionFactorState: Yup.object({
            mandatory: Yup.boolean().default(false),
          }),
          unitKeyState: Yup.object({
            mandatory: Yup.boolean().default(false),
          }),
        })
        .nullable(),
    }),
  };
};

export const step1ValidationSchema = (
  verificationType?: VerificationType,
  verifiersDataSource?: EPDVerifierModel[],
  isLoadingVerifiers?: boolean
) =>
  Yup.object<Partial<EPDModel>>({
    ...validatedString('title'),
    ...validatedEPDDeveloper('epdDevelopers'),
    ...validatedSubObject('preverifiedTool'),
    ...validatedSubObject('preverifiedToolVersion'),
    ...validatedEPDVerifier('epdVerifier', verificationType, verifiersDataSource, isLoadingVerifiers),
    ...validatedSubObject('pcr'),
    ...validatedString('productCategoryId'),
    ...validatedString('contactPerson'),
  });

export const step2ValidationSchema = Yup.object<Partial<EPDModel>>({
  // Unable to validate subtype since it's not matching the state and value
  // fields in case. Also as a string enum there is no simple way to validate
  // since string constraints will not accept a string enum.
  ...validatedDocuments('documents'),
  ...validatedString('productDescription'),
  ...validatedString('gtin'),
  ...validatedString('products'),
  ...validatedArray('productImages'),
  ...validatedArray('lcAs'),
  ...validateReferenceFlow('referenceFlow'),
  ...validatedArray('materialProperties'),
  ...validatedArray('geographicalScopes'),
  ...validatedString('statementOfConformityWithStandards'),
  ...validatedString('geographicalDescription'),
  ...validatedString('technologyDescription'),
  ...validatedString('technicalPurpose'),
  ...validatedDataSourcesDictionary('epdDataSources'),
  ...validatedString('referencePackageVersion15804'),
  ...validatedString('characterisationMethods'),
  ...validatedString('useAdvice'),
  ...validatedSubtype('subtype'),
  ...validatedString('functionalUnitDescription'),
  ...validatedEnum('epdBasedOnUnit', EPDBasedOnUnitEnum),
  ...validatedProductType('productType'),
  ...validatedConversionFactorToMass('conversionFactorToMass'),
  ...validatedNumber('massOfDeclaredUnit'),
  // US #9798: Temporarily removed everything Okobaudat-related
  // ...validatedString('requirePublicationOnOkobaudat'),
  ...validatedString('okobaudatProductCategoryId'),
  ...validatedString('okobaudatProductSubCategoryId'),
  ...validatedString('okobaudatProductCategoryTypeId'),
  ...validatedBiogenicCarbonContent('biogenicCarbonContent'),
  ...mustBeEmptyErrorString('materialProperties'),
  ...validatedString('otherDatabaseUsedAsDatasourceDescription'),
});

export const step3ValidationSchema = Yup.object<Partial<EPDModel>>({
  ...validatedArray('verificationReports'),
  ...validatedArray('processCertificates'),
});

export const step4ValidationSchema = Yup.object<Partial<EPDModel>>({
  ...validatedDate('firstPublishedVersionDate'),
  ...validatedDate('versionDate'),
  ...validatedValidityDate('validityDate'),
});

export const isValidIgnoringWarnings = (schema: Yup.ObjectSchema, epd: EPDModel): boolean => {
  try {
    schema.validateSync(epd, { abortEarly: false });
    return true;
  } catch (err: any) {
    /// for now warnings are considered as error too
    return false;

    // if (!(err instanceof Yup.ValidationError) || !err.inner) {
    //   return false;
    // }

    // const epdRecord = epd as unknown as Record<string, unknown>;
    // return err.inner.every((validationError: Yup.ValidationError) => {
    //   const fieldName = validationError.path;
    //   if (!fieldName) {
    //     return false;
    //   }

    //   const fieldState = epdRecord[`${fieldName}State`] as FieldState | undefined;
    //   const severity = fieldState?.errorSeverity;
    //   return severity === 'warning' || severity === 'information';
    // });
  }
};
